import { InjectionToken } from '@angular/core';

export type AppEnv = Record<string, any>;

const parseRuntimeValue = (raw: string): any => {
    const trimmed = raw.trim();

    if (/^(true|false)$/i.test(trimmed)) {
        return trimmed.toLowerCase() === 'true';
    }

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
            return JSON.parse(trimmed);
        } catch {
            return trimmed;
        }
    }

    return trimmed;
};

const parseLegacyEnvConfigFragment = (raw: string): Record<string, any> => {
    const fragment = raw.trim();
    const match = fragment.match(/^\{?\s*([\w.-]+)\s*=\s*(\{.*\})\s*,?\s*$/s);

    if (!match) {
        return {};
    }

    const key = match[1];
    const value = match[2];

    try {
        return { [key]: JSON.parse(value) };
    } catch {
        return { [key]: value };
    }
};

const buildFromProcessEnv = (): AppEnv => {
    const processEnv = (globalThis as any)?.process?.env as Record<string, string | undefined> | undefined;
    if (!processEnv) {
        return {} as AppEnv;
    }

    const config: AppEnv = {};
    const rawEnvConfig = processEnv['ENV_CONFIG'];

    if (rawEnvConfig) {
        try {
            const parsed = JSON.parse(rawEnvConfig);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                Object.assign(config, parsed);
            }
        } catch {
            Object.assign(config, parseLegacyEnvConfigFragment(rawEnvConfig));
        }
    }

    for (const [key, value] of Object.entries(processEnv)) {
        if (!value || key === 'ENV_CONFIG') {
            continue;
        }

        config[key] = parseRuntimeValue(value);
    }

    return config;
};

export const APP_ENV = new InjectionToken<AppEnv>('APP_ENV', {
    providedIn: 'root',
    factory: () => buildFromProcessEnv(),
});